import Phaser from 'phaser';
import { LocalStorage } from '../../utils/localStorage';
import { GameMode } from '../../types/GameMode';
import { DifficultyPreset } from '../../types/DifficultyPreset';
import { GamepadHelper } from '../../utils/gamepadHelper';

type DebugMenuItem = {
  label: string;
  scene: string;
  shortcut?: {
    display: string;
    phaserKeydownEvent: string;
  };
  data?: Record<string, any>;
};

export class DebugMenuScene extends Phaser.Scene {
  private menuItems: DebugMenuItem[] = [
    { label: 'Controller Debug', scene: 'ControllerDebugScene', shortcut: { display: '1', phaserKeydownEvent: 'keydown-ONE' } },
    { label: 'Player Movement & Shooting', scene: 'PlayerTestScene', shortcut: { display: '2', phaserKeydownEvent: 'keydown-TWO' } },
    { label: 'Armada Movement', scene: 'ArmadaTestScene', shortcut: { display: '3', phaserKeydownEvent: 'keydown-THREE' } },
    { label: 'Bullet Collisions', scene: 'CollisionTestScene', shortcut: { display: '4', phaserKeydownEvent: 'keydown-FOUR' } },
    { label: 'Camera Capture to Sprite', scene: 'CameraTestScene', shortcut: { display: '5', phaserKeydownEvent: 'keydown-FIVE' } },
    { label: 'Webcam Debug', scene: 'WebcamDebugScene', shortcut: { display: 'V', phaserKeydownEvent: 'keydown-V' } },
    { label: 'Sprite Debug (SVG & Faces)', scene: 'SpriteDebugScene', shortcut: { display: '6', phaserKeydownEvent: 'keydown-SIX' } },
    { label: 'Compare: Game vs Sprite Debug', scene: 'CompareScene', shortcut: { display: '7', phaserKeydownEvent: 'keydown-SEVEN' } },
    { label: 'Stored Faces', scene: 'StoredFacesScene', shortcut: { display: '8', phaserKeydownEvent: 'keydown-EIGHT' } },
    { label: 'Abduction Animation', scene: 'AbductionScene', shortcut: { display: '9', phaserKeydownEvent: 'keydown-NINE' } },
    { label: 'Abduction Line', scene: 'AbductionLineScene', shortcut: { display: '0', phaserKeydownEvent: 'keydown-ZERO' } },
    { label: 'Bomb Drop Test', scene: 'BombTestScene', shortcut: { display: 'B', phaserKeydownEvent: 'keydown-B' } },
    { label: 'Path Test (Attack Paths)', scene: 'PathTestScene', shortcut: { display: 'P', phaserKeydownEvent: 'keydown-P' } },
    { label: 'Radar Intro (Narrative)', scene: 'RadarIntroScene', shortcut: { display: 'R', phaserKeydownEvent: 'keydown-R' }, data: { nextScene: 'MenuScene', payload: {} } },
    { label: 'Space Invaders Intro', scene: 'SpaceInvadersIntroScene', data: { toMode: GameMode.SPACE_INVADERS, level: 1, score: 0, useWebcam: false, lives: 3 } },
    { label: 'Galaga Intro', scene: 'GalagaIntroScene', data: { toMode: GameMode.GALAGA, level: 1, score: 0, useWebcam: false, lives: 3 } },
    { label: 'Mode Transition (SI→Galaga)', scene: 'ModeTransitionScene', data: { fromMode: GameMode.SPACE_INVADERS, toMode: GameMode.GALAGA, level: 1, score: 0, useWebcam: false, lives: 3, difficulty: DifficultyPreset.MEDIUM, advanceLevel: false } },
    { label: 'Invaders → Galaga Transition', scene: 'GalagaSkyTransitionScene', shortcut: { display: 'G', phaserKeydownEvent: 'keydown-G' }, data: { fromMode: GameMode.SPACE_INVADERS, toMode: GameMode.GALAGA, level: 1, score: 0, useWebcam: false, lives: 3, difficulty: DifficultyPreset.MEDIUM, advanceLevel: false, showDefenderPreview: true } },
    { label: 'System Check', scene: 'SystemCheckScene' },
    // Balance Testing Quick Starts (7.2)
    { label: 'Start SI Level 1', scene: 'SpaceInvadersScene', shortcut: { display: 'Q', phaserKeydownEvent: 'keydown-Q' }, data: { level: 1, score: 0, useWebcam: false, startMode: GameMode.SPACE_INVADERS } },
    { label: 'Start SI Level 5', scene: 'SpaceInvadersScene', shortcut: { display: 'W', phaserKeydownEvent: 'keydown-W' }, data: { level: 5, score: 0, useWebcam: false, startMode: GameMode.SPACE_INVADERS } },
    { label: 'Start SI Level 10', scene: 'SpaceInvadersScene', shortcut: { display: 'E', phaserKeydownEvent: 'keydown-E' }, data: { level: 10, score: 0, useWebcam: false, startMode: GameMode.SPACE_INVADERS } },
    { label: 'Start Galaga Level 1', scene: 'GalagaScene', shortcut: { display: 'A', phaserKeydownEvent: 'keydown-A' }, data: { level: 1, score: 0, useWebcam: false, startMode: GameMode.GALAGA } },
    { label: 'Start Galaga Level 5', scene: 'GalagaScene', shortcut: { display: 'S', phaserKeydownEvent: 'keydown-S' }, data: { level: 5, score: 0, useWebcam: false, startMode: GameMode.GALAGA } },
    { label: 'Start Galaga Level 10', scene: 'GalagaScene', shortcut: { display: 'D', phaserKeydownEvent: 'keydown-D' }, data: { level: 10, score: 0, useWebcam: false, startMode: GameMode.GALAGA } },
    { label: 'Mobile Touch Controls', scene: 'MobileDebugScene', shortcut: { display: 'M', phaserKeydownEvent: 'keydown-M' } },
    { label: 'Back to Main Menu', scene: 'MenuScene', shortcut: { display: 'ESC', phaserKeydownEvent: 'keydown-ESC' } }
  ];

  private textObjects: Phaser.GameObjects.Text[] = [];
  private selectedIndex: number = 0;

  private scrollOffset: number = 0;
  private itemsPerPage: number = 0;
  private menuStartY: number = 150;
  private menuItemSpacing: number = 40;
  
  // Input state
  private gamepad: Phaser.Input.Gamepad.Gamepad | null = null;
  private prevUp: boolean = false;
  private prevDown: boolean = false;
  private prevFire: boolean = false;
  private prevBack: boolean = false;
  private lastStickMove: number = 0;

  private backButtonIndex!: number;
  private fireButtonIndex!: number;

  private initialUpdateEvent: Phaser.Time.TimerEvent | null = null;
  private inactivityTimer?: Phaser.Time.TimerEvent;
  private resetInactivityHandler?: () => void;

  constructor() {
    super({ key: 'DebugMenuScene' });
  }

  create(): void {
    this.resetState();

    const settings = LocalStorage.getSettings();
    this.backButtonIndex = settings.controllerBackButton!;
    this.fireButtonIndex = settings.controllerFireButton!;

    const { width, height } = this.cameras.main;

    this.add.text(width / 2, 60, 'DEBUG MENU', {
      fontSize: '36px',
      fontFamily: 'Courier New',
      color: '#00ff00'
    }).setOrigin(0.5);

    this.itemsPerPage = Math.max(1, Math.floor((height - this.menuStartY - 40) / this.menuItemSpacing));

    this.menuItems.forEach((item, i) => {
      const prefix = item.shortcut ? `${item.shortcut.display} - ` : '';
      const text = this.add.text(width / 2, this.menuStartY + i * this.menuItemSpacing, `${prefix}${item.label}`, {
        fontSize: '22px',
        fontFamily: 'Courier New',
        color: '#ffffff'
      }).setOrigin(0.5);
      
      this.textObjects.push(text);
    });

    this.setupKeyboard();

    this.input.on('wheel', this.onWheel, this);
    
    // Initial connection check
    if (this.input.gamepad && this.input.gamepad.total > 0) {
      this.gamepad = this.input.gamepad.getPad(0);
    }

    if (this.gamepad && this.gamepad.connected) {
      this.prevFire = !!(this.gamepad.A || this.gamepad.buttons[0]?.pressed);
      this.prevBack = !!this.gamepad.buttons[this.backButtonIndex]?.pressed;
    }

    this.initialUpdateEvent = this.time.delayedCall(0, () => {
      this.updateMenuView();
    });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown, this);

    this.startInactivityTimeout();
  }

  update(): void {
    this.handleGamepadInput();
  }

  private startExclusive(targetSceneKey: string, data?: any): void {
    const scenes = this.scene.manager.getScenes(true) as Phaser.Scene[];
    scenes.forEach((s: Phaser.Scene) => {
      const key = s.scene.key;
      if (key !== targetSceneKey) {
        this.scene.stop(key);
      }
    });

    this.scene.start(targetSceneKey, data);
  }

  private setupKeyboard(): void {
    this.menuItems.forEach((item, index) => {
      if (!item.shortcut) return;

      this.input.keyboard?.on(item.shortcut.phaserKeydownEvent, () => {
        this.launchScene(index);
      });
    });

    // Navigation keys
    this.input.keyboard?.on('keydown-UP', () => this.moveSelection(-1));
    this.input.keyboard?.on('keydown-DOWN', () => this.moveSelection(1));
    this.input.keyboard?.on('keydown-ENTER', () => this.launchScene(this.selectedIndex));

    this.input.keyboard?.on('keydown-PAGEUP', () => this.scrollBy(-this.itemsPerPage));
    this.input.keyboard?.on('keydown-PAGEDOWN', () => this.scrollBy(this.itemsPerPage));
  }

  private resetState(): void {
    if (this.initialUpdateEvent) {
      this.initialUpdateEvent.remove(false);
      this.initialUpdateEvent = null;
    }

    this.input.off('wheel', this.onWheel, this);
    this.input.keyboard?.removeAllListeners();

    this.textObjects.forEach((text) => text.destroy());
    this.textObjects = [];
    this.selectedIndex = 0;
    this.scrollOffset = 0;

    this.gamepad = null;
    this.prevUp = false;
    this.prevDown = false;
    this.prevFire = false;
    this.prevBack = false;
    this.lastStickMove = 0;
  }

  private onShutdown(): void {
    if (this.initialUpdateEvent) {
      this.initialUpdateEvent.remove(false);
      this.initialUpdateEvent = null;
    }

    this.input.off('wheel', this.onWheel, this);
    this.input.keyboard?.removeAllListeners();

    this.inactivityTimer?.remove(false);
    if (this.resetInactivityHandler) {
      this.input.off('pointerdown', this.resetInactivityHandler);
      this.input.keyboard?.off('keydown', this.resetInactivityHandler);
      this.input.gamepad?.off('down', this.resetInactivityHandler);
    }

    this.textObjects.forEach((text) => text.destroy());
    this.textObjects = [];
  }

  private onWheel(_pointer: Phaser.Input.Pointer, _over: unknown[], _dx: number, dy: number): void {
    const direction = dy > 0 ? 1 : -1;
    this.scrollBy(direction);
  }

  private handleGamepadInput(): void {
    if (!this.input.gamepad) return;
    
    // Refresh gamepad if needed
    if (!this.gamepad || !this.gamepad.connected) {
      this.gamepad = this.input.gamepad.getPad(0);
    }
    
    if (!this.gamepad || !this.gamepad.connected) return;

    const now = Date.now();
    const axisY = this.gamepad.axes[1].getValue();
    const dpadUp = this.gamepad.up;
    const dpadDown = this.gamepad.down;

    // Up
    const isUp = dpadUp || axisY < -0.5;
    if (isUp && !this.prevUp) {
      if (now - this.lastStickMove > 200) {
        this.moveSelection(-1);
        this.lastStickMove = now;
      }
    }
    this.prevUp = isUp;

    // Down
    const isDown = dpadDown || axisY > 0.5;
    if (isDown && !this.prevDown) {
      if (now - this.lastStickMove > 200) {
        this.moveSelection(1);
        this.lastStickMove = now;
      }
    }
    this.prevDown = isDown;

    // Select (configured fire button only)
    const isFire = !!GamepadHelper.isButtonPressed(this.gamepad, this.fireButtonIndex);
    if (isFire && !this.prevFire) {
      this.launchScene(this.selectedIndex);
    }
    this.prevFire = isFire;
    
    // Back (configured button index)
    const isBack = !!GamepadHelper.isButtonPressed(this.gamepad, this.backButtonIndex);
    if (isBack && !this.prevBack) {
      this.startExclusive('MenuScene');
    }
    this.prevBack = isBack;
  }

  private startInactivityTimeout(): void {
    const reset = () => {
      this.inactivityTimer?.remove(false);
      this.inactivityTimer = this.time.delayedCall(60000, () => {
        this.startExclusive('MenuScene');
      });
    };

    this.resetInactivityHandler = reset;
    this.input.on('pointerdown', reset);
    this.input.keyboard?.on('keydown', reset);
    this.input.gamepad?.on('down', reset);
    reset();
  }

  private moveSelection(delta: number): void {
    // Update index
    this.selectedIndex = (this.selectedIndex + delta + this.menuItems.length) % this.menuItems.length;

    this.ensureSelectedVisible();
    this.updateMenuView();
  }

  private scrollBy(delta: number): void {
    if (this.menuItems.length <= this.itemsPerPage) return;

    const maxOffset = Math.max(0, this.menuItems.length - this.itemsPerPage);
    this.scrollOffset = Phaser.Math.Clamp(this.scrollOffset + delta, 0, maxOffset);
    this.updateMenuView();
  }

  private ensureSelectedVisible(): void {
    if (this.menuItems.length <= this.itemsPerPage) {
      this.scrollOffset = 0;
      return;
    }

    const maxOffset = Math.max(0, this.menuItems.length - this.itemsPerPage);
    if (this.selectedIndex < this.scrollOffset) {
      this.scrollOffset = this.selectedIndex;
    } else if (this.selectedIndex >= this.scrollOffset + this.itemsPerPage) {
      this.scrollOffset = this.selectedIndex - this.itemsPerPage + 1;
    }

    this.scrollOffset = Phaser.Math.Clamp(this.scrollOffset, 0, maxOffset);
  }

  private updateMenuView(): void {
    const { width } = this.cameras.main;

    this.textObjects.forEach((text, i) => {
      const row = i - this.scrollOffset;
      const visible = row >= 0 && row < this.itemsPerPage;
      text.setVisible(visible);
      if (visible) {
        text.setPosition(width / 2, this.menuStartY + row * this.menuItemSpacing);
        if (i === this.selectedIndex) {
          text.setTint(0xffff00);
        } else {
          text.clearTint();
        }
      }
    });
  }

  private launchScene(index: number): void {
    const item = this.menuItems[index];
    if (item) {
      this.startExclusive(item.scene, item.data);
    }
  }
}
